/**
 * Product quickview — opens the REAL product page inside a <dialog>.
 *
 * A trigger anywhere on any page (`[data-aero-quickview-open]` + `data-product-url`)
 * fetches that product's `aero-main-product` section and injects it into the shared
 * shell from snippets/aero-extension-product-quickview.liquid.
 *
 * Why fetch the real section instead of rendering a bespoke quickview:
 * the request goes to a genuine product URL, so `product`, `customer` and
 * `localization` are all in scope server-side. Retail-vs-B2B gating, the US/CA market
 * check, the AH-RET-NA variant resolution and the Amazon fallback therefore evaluate
 * exactly as they do on the PDP — none of it is reimplemented here, so none of it can
 * drift.
 *
 * See documentation/product-quickview.md.
 */
(function () {
  'use strict';

  var SELECTORS = {
    trigger: '[data-aero-quickview-open]',
    dialog: '[data-aero-quickview]',
    body: '[data-aero-quickview-body]',
    skeleton: '[data-aero-quickview-skeleton]',
    title: '#AeroQuickviewTitle'
  };

  /**
   * The quickview is fetched as a TEMPLATE render, not through the Section Rendering
   * API. `templates/product.aero-quickview.json` carries `"layout": false`, so the
   * response is the bare section markup — same ~33KB as the old section_id fetch, no
   * header/footer.
   *
   * The old `&section_id=aero-quickview-product` form is gone on purpose. It rendered
   * the section DETACHED from the template JSON: `section.blocks.size` came back 0, so
   * app blocks placed in the theme editor (Buy with Prime's Prime delivery card) never
   * appeared in the dialog while rendering fine on the PDP. Shopify only applies
   * template settings/blocks when section_id is the qualified `template--<id>__<key>`
   * form, and that id is per-theme, so it cannot be hardcoded here. Fetching the
   * template sidesteps the problem entirely.
   *
   * Renaming templates/product.aero-quickview.json breaks the quickview — the dialog
   * then shows its in-dialog error state and logs to the console.
   */
  var TEMPLATE_VIEW = 'aero-quickview';

  var dialog;
  var body;
  var titleEl;
  var skeletonTemplate;

  var currentInstance = null; // teardown handle from AeroProductPage.init
  var inFlight = null; // AbortController for the active fetch
  var openCounter = 0; // suffixes injected ids so repeat opens stay unique

  /**
   * Make every id in the injected fragment unique.
   *
   * The PDP section assumes it is alone on the page. Rendering it inside the dialog
   * duplicates `accordion-description`, `accordion-includes`, `accordion-description-2`,
   * `product_form_{id}`, `quantity`, and the ids kept on the media/carousel/variant
   * elements. The accordions are the ones that actually break: they are the CSS
   * checkbox hack, so a `<label for>` pointing at a duplicated id toggles the wrong
   * panel — or the PDP's panel behind the dialog.
   *
   * Labels are re-pointed within their own `.air-accordion__item` rather than by
   * document order. That also fixes a pre-existing PDP bug where the section renders
   * `accordion-description-2` twice (desktop + mobile copies), so both labels bind to
   * the first input and the mobile label toggles the hidden desktop panel.
   */
  function scopeIds(root, suffix) {
    root.querySelectorAll('[id]').forEach(function (el) {
      // getAttribute, NOT el.id. On a <form>, named controls are exposed as
      // properties of the form, so a form containing <input name="id"> (which the
      // PDP's add-to-cart form does — it carries the variant id) has `form.id`
      // resolve to that INPUT ELEMENT rather than the form's id string. Reading the
      // attribute sidesteps the shadowing entirely.
      var oldId = el.getAttribute('id');
      if (!oldId) return;

      var newId = oldId + '-' + suffix;
      el.setAttribute('id', newId);

      // Prefer a label inside the same accordion item; fall back to fragment scope.
      var scope = el.closest('.air-accordion__item') || root;
      var labels = scope.querySelectorAll('label[for="' + oldId.replace(/"/g, '\\"') + '"]');
      labels.forEach(function (label) {
        label.setAttribute('for', newId);
      });
    });
  }

  function showSkeleton() {
    body.innerHTML = '';
    if (skeletonTemplate && skeletonTemplate.content) {
      body.appendChild(skeletonTemplate.content.cloneNode(true));
    }
    body.setAttribute('aria-busy', 'true');
  }

  /**
   * Render an in-dialog failure state.
   *
   * The dialog never navigates on its own. If the section cannot be loaded the
   * shopper sees a short message plus an explicit link to the full product page —
   * their choice to follow it, not ours.
   */
  function renderError(productUrl) {
    body.setAttribute('aria-busy', 'false');
    body.innerHTML = '';

    var wrap = document.createElement('div');
    wrap.className = 'air-empty-state';

    var title = document.createElement('p');
    title.className = 'air-empty-state__title';
    title.textContent = 'This product could not be loaded';

    var description = document.createElement('p');
    description.className = 'air-empty-state__description';
    description.textContent = 'Please close this window and try again.';

    var actions = document.createElement('div');
    actions.className = 'air-empty-state__actions';

    var link = document.createElement('a');
    link.className = 'air-btn air-btn--primary air-btn--sm';
    link.href = productUrl;
    link.textContent = 'View full product page';

    actions.appendChild(link);
    wrap.appendChild(title);
    wrap.appendChild(description);
    wrap.appendChild(actions);
    body.appendChild(wrap);
  }

  /**
   * Announce that a product was viewed in the quickview.
   *
   * Opening the dialog fires no pageview, so without this a quickview view is
   * invisible to any analytics that counts PDP visits — the shopper sees the full
   * product page but the funnel shows nothing between the article and the cart.
   *
   * Two channels, so neither has to exist for the other to work:
   *   - `aero:quickview:open` DOM event, matching the theme's `aero:cart:*` convention.
   *   - a `dataLayer` push, picked up by GTM when it is enabled (it is currently
   *     commented out in aero-code-inject-head-start.liquid, so this is inert until
   *     someone turns it back on — pushing to the array is safe regardless).
   *
   * Detail is read from the injected markup rather than passed in, so it reflects what
   * the shopper actually sees.
   */
  function announceOpen(productUrl, container) {
    var titleNode = container.querySelector('.air-product__title');
    var priceNode = container.querySelector('.air-product__price');
    var variantNode = container.querySelector('[data-aero-variant-select]');

    var detail = {
      productUrl: productUrl,
      handle: (productUrl.split('/products/')[1] || '').split('?')[0],
      title: titleNode ? titleNode.textContent.trim() : null,
      price: priceNode ? priceNode.textContent.trim() : null,
      variantId: variantNode ? variantNode.value : null
    };

    try {
      document.dispatchEvent(new CustomEvent('aero:quickview:open', { detail: detail }));
    } catch (e) {
      /* CustomEvent unavailable — the dataLayer push below still runs. */
    }

    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'aero_quickview_open', quickview: detail });
    } catch (e) {
      /* Never let analytics break the dialog. */
    }
  }

  function teardownInstance() {
    if (currentInstance && typeof currentInstance.destroy === 'function') {
      currentInstance.destroy();
    }
    currentInstance = null;
  }

  function closeDialog() {
    if (dialog.open && typeof dialog.close === 'function') dialog.close();
  }

  function openDialog() {
    if (window.Airways && window.Airways.dialog && window.Airways.dialog.open) {
      window.Airways.dialog.open('#' + dialog.id);
      return;
    }
    // Behaviour script missing/failed — still open rather than silently doing nothing.
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
  }

  function sectionUrlFor(productUrl) {
    // `view=aero-quickview` selects templates/product.aero-quickview.json, which holds
    // only the quickview section and renders with `"layout": false`. Preserve any
    // existing query (e.g. ?variant=).
    var separator = productUrl.indexOf('?') === -1 ? '?' : '&';
    return productUrl + separator + 'view=' + encodeURIComponent(TEMPLATE_VIEW);
  }

  function onTriggerClick(event) {
    var trigger = event.target.closest(SELECTORS.trigger);
    if (!trigger || !dialog || !body) return;

    var productUrl = trigger.getAttribute('data-product-url') || trigger.getAttribute('href');
    if (!productUrl) return;

    // Let modified clicks (new tab/window) and non-primary buttons behave natively.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

    event.preventDefault();

    // Rapid clicks across a grid must not race — the last one wins.
    if (inFlight) inFlight.abort();
    var controller = new AbortController();
    inFlight = controller;

    teardownInstance();
    showSkeleton();

    // Open BEFORE the fetch resolves: the interaction feels instant and the native
    // dialog traps focus from this moment.
    //
    // Deferred to the next task rather than opened synchronously. Our listener is on
    // `document`, so by the time it runs the click is already at the document level —
    // stopPropagation cannot retroactively hide it from a dialog that does not exist
    // yet. Opening on the next tick means the dialog appears AFTER this click event
    // has fully finished dispatching, so it can never be seen by the DS backdrop
    // handler (`event.target === dialog` → close) that would otherwise close the
    // dialog with the very click that opened it.
    setTimeout(openDialog, 0);

    fetch(sectionUrlFor(productUrl), {
      headers: { Accept: 'text/html' },
      signal: controller.signal
    })
      .then(function (response) {
        if (!response.ok) throw new Error('quickview fetch failed: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        if (controller.signal.aborted) return;
        if (!html || !html.trim()) throw new Error('quickview fetch returned empty body');

        body.innerHTML = html;
        body.setAttribute('aria-busy', 'false');

        var suffix = 'qv' + ++openCounter;
        scopeIds(body, suffix);

        // Defensive: the quickview section carries no page-level section padding, but
        // strip it if one is ever added, so it cannot double up against the dialog's.
        body.querySelectorAll('.air-section__padding').forEach(function (el) {
          el.classList.remove('air-section__padding');
        });

        // Everything past this point is ENHANCEMENT of already-visible content, so it
        // must not reach the .catch below — that catch replaces the body with an error
        // state, and a throw in (say) the buy-box wiring would wipe a perfectly good
        // product view. Only fetch/inject failures should surface the error state.
        try {
          // Give the dialog the product's name for assistive tech.
          var heading = body.querySelector('.air-product__title, h1');
          if (heading && titleEl) titleEl.textContent = heading.textContent.trim();

          // Hand the injected section to the shared buy-box behaviour, scoped to the
          // dialog. updateUrl:false — this is an overlay, not the page the shopper is
          // on, so it must not rewrite the address bar.
          if (window.AeroProductPage && window.AeroProductPage.init) {
            var section = body.querySelector('.shopify-section') || body;
            currentInstance = window.AeroProductPage.init(section, { updateUrl: false });
          }

          announceOpen(productUrl, body);
        } catch (enhanceError) {
          // Dialog stays open with the product visible; only the interactive
          // enhancements are degraded.
          if (window.console && console.warn) {
            console.warn('[quickview] post-inject enhancement failed', enhanceError);
          }
        }
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') return;

        // Once the dialog is open the shopper stays in it — close or add to cart are
        // the only exits. Navigating away mid-interaction is exactly the jarring
        // behaviour this feature exists to avoid, so a failure renders an in-dialog
        // error with a link the shopper may choose to follow, rather than yanking
        // them to the PDP.
        if (window.console && console.error) {
          console.error('[quickview] load failed:', error);
        }
        renderError(productUrl);
      })
      .finally(function () {
        if (inFlight === controller) inFlight = null;
      });
  }

  function init() {
    dialog = document.querySelector(SELECTORS.dialog);
    if (!dialog) return;

    body = dialog.querySelector(SELECTORS.body);
    titleEl = dialog.querySelector(SELECTORS.title);
    skeletonTemplate = document.querySelector(SELECTORS.skeleton);

    document.addEventListener('click', onTriggerClick);

    // `close` fires for every path — close button, Escape, backdrop click — so one
    // handler covers them all.
    dialog.addEventListener('close', function () {
      if (inFlight) {
        inFlight.abort();
        inFlight = null;
      }
      teardownInstance();
      body.innerHTML = '';
      body.setAttribute('aria-busy', 'false');
      if (titleEl) titleEl.textContent = 'Product details';
    });

    // The add-to-cart form inside the dialog carries [data-aero-cart-add-form], so
    // aero-component-cart-drawer.js AJAXes it with no code here. It dispatches this
    // before opening the drawer; close first so the drawer is not hidden behind a
    // top-layer modal. A FAILED add never fires this, so the dialog stays open.
    document.addEventListener('aero:cart:added', function (event) {
      if (!dialog.open) return;
      var form = event.detail && event.detail.form;
      if (form && !dialog.contains(form)) return;
      closeDialog();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
